import useAnimatedCounter from "./useAnimatedCounter";
import { REGIONS } from "./trainingData";

export default function StatsSection() {
  const { counts, ref } = useAnimatedCounter({
    gap: 4.8,
    breach: 4.44,
    growth: 29,
    days: 241,
  });

  const stats = [
    {
      value: `${counts.gap.toFixed(1)}M`,
      color: "text-white",
      label: "Unfilled cybersecurity roles worldwide",
      source: "A record global workforce gap, up 19% year on year.",
      org: "ISC2 Workforce Study",
    },
    {
      value: `$${counts.breach.toFixed(2)}M`,
      color: "text-[#ef4444]",
      label: "Average cost of one data breach",
      source: "Global average, 2025. In the US it is $10.22M.",
      org: "IBM Cost of a Data Breach 2025",
    },
    {
      value: `+${Math.round(counts.growth)}%`,
      color: "text-[#a3e635]",
      label: "Projected growth for security analysts",
      source: "2024\u20132034, far faster than all occupations.",
      org: "US Bureau of Labor Statistics",
    },
    {
      value: Math.round(counts.days),
      color: "text-white",
      label: "Days to spot and contain a breach",
      source: "Mean time to identify + contain. Skilled teams cut it.",
      org: "IBM, 2025",
    },
  ];

  return (
    <section
      ref={ref}
      className="border-t border-b border-[#18181b] bg-[#050505]"
    >
      <div className="max-w-[1200px] mx-auto px-6 py-10 sm:py-20">
        <div className="max-w-[760px] mb-6 sm:mb-14">
          <div
            className="font-mono text-[12px] text-[#71717a] mb-[18px]"
            style={{ letterSpacing: "0.18em" }}
          >
            01 — WHY THIS MATTERS
          </div>
          <h2
            className="font-bold text-white mb-[18px]"
            style={{
              fontSize: "clamp(30px, 3.7vw, 46px)",
              lineHeight: 1.06,
              letterSpacing: "-0.03em",
            }}
          >
            The defenders are outnumbered. That is the whole opportunity.
          </h2>
          <p className="text-[16px] sm:text-[18px] leading-[1.65] text-[#a1a1aa]">
            Attacks scale with automation. Defence still scales with people
            &mdash; and there aren&apos;t enough trained ones. Every number
            below is a job that someone has to be qualified to take.
          </p>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-px bg-[#27272a] border border-[#27272a] rounded-xl overflow-hidden mb-5">
          {stats.map((s, i) => (
            <div key={i} className="bg-black p-5 sm:p-8">
              <div
                className={`text-[36px] sm:text-[52px] font-bold leading-none ${s.color}`}
                style={{ letterSpacing: "-0.03em" }}
              >
                {s.value}
              </div>
              <div className="text-[15px] font-semibold mt-2.5 mb-2">
                {s.label}
              </div>
              <div className="font-mono text-[11px] text-[#71717a] leading-relaxed">
                {s.source}
                <br />
                {s.org}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6 sm:mb-14">
          <div className="border border-[#27272a] rounded-xl p-7 bg-black">
            <div
              className="text-[26px] sm:text-[34px] font-bold"
              style={{ letterSpacing: "-0.02em" }}
            >
              95%
            </div>
            <div className="text-[15px] text-[#d4d4d8] mt-2 leading-relaxed">
              of security teams report at least one skills gap on the team
              &mdash; and 59% now call that gap critical or significant, up
              from 44% a year earlier.
            </div>
            <div className="font-mono text-[11px] text-[#71717a] mt-3">
              ISC2 CYBERSECURITY WORKFORCE STUDY 2025
            </div>
          </div>
          <div className="border border-[#27272a] rounded-xl p-7 bg-black">
            <div
              className="text-[26px] sm:text-[34px] font-bold text-[#ef4444]"
              style={{ letterSpacing: "-0.02em" }}
            >
              +$1.57M
            </div>
            <div className="text-[15px] text-[#d4d4d8] mt-2 leading-relaxed">
              added to the cost of a breach when an organisation is short of
              security skills. Companies are not hiring out of goodwill
              &mdash; untrained is expensive.
            </div>
            <div className="font-mono text-[11px] text-[#71717a] mt-3">
              IBM COST OF A DATA BREACH 2025
            </div>
          </div>
        </div>

        <div
          className="font-mono text-[12px] text-[#71717a] mb-6"
          style={{ letterSpacing: "0.18em" }}
        >
          DEMAND BY REGION — WHERE OUR STUDENTS GET HIRED
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-4">
          {REGIONS.map((r, i) => (
            <div
              key={i}
              className="border border-[#27272a] rounded-[10px] p-5 bg-black"
            >
              <div className="text-[16px] font-semibold mb-3.5">
                {r.name}
              </div>
              <div className="h-1 bg-[#27272a] rounded-sm overflow-hidden mb-3.5">
                <div
                  className="h-full bg-[#a3e635] rounded-sm"
                  style={{ width: `${r.pct}%` }}
                />
              </div>
              <div className="text-[13px] text-[#a1a1aa] leading-relaxed">
                {r.note}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
